import React from 'react';
import { useParams } from 'react-router-dom';
import { LiveMatchWebhookProvider, useLiveMatchWebhook } from '../context/LiveMatchWebhookContext';
import LiveMatchView from '../components/LiveMatchView';
import '../styles/LiveMatchPage.scss';

// Inner content component that uses the context
const LiveMatchContent: React.FC = () => {
  const { liveState, isLoading, error, refresh } = useLiveMatchWebhook();

  if (isLoading && !liveState) {
    return (
      <div className="live-match-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading live match data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-match-page">
        <div className="error-container">
          <h2>Error Loading Match</h2>
          <p>{error}</p>
          <button onClick={refresh} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!liveState) {
    return (
      <div className="live-match-page">
        <div className="no-match-container">
          <h2>No Live Match Data</h2>
          <p>This match hasn't started yet or no data is available.</p>
          <button onClick={refresh} className="refresh-button">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="live-match-page">
      <LiveMatchView />

      {/* Manual Refresh Button (for debugging/force refresh) */}
      {liveState && (
        <div className="debug-actions" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px',
          marginTop: '20px',
          padding: '15px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '8px'
        }}>
          <button
            onClick={refresh}
            className="refresh-button"
            style={{
              background: 'rgba(0, 217, 255, 0.1)',
              border: '2px solid rgba(0, 217, 255, 0.3)',
              color: '#00d9ff',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease'
            }}
          >
            🔄 Force Refresh
          </button>
          <span style={{ color: '#999', fontSize: '14px' }}>
            Last updated: {liveState.updated_at ? new Date(liveState.updated_at).toLocaleTimeString() : 'Never'}
          </span>
        </div>
      )}
    </div>
  );
};

// Main page component with provider
export const LiveMatchWebhookPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();

  if (!matchId) {
    return (
      <div className="live-match-page">
        <div className="error-container">
          <h2>Invalid Match ID</h2>
          <p>No match ID provided in the URL</p>
        </div>
      </div>
    );
  }

  return (
    <LiveMatchWebhookProvider matchId={matchId}>
      <LiveMatchContent />
    </LiveMatchWebhookProvider>
  );
};
